import {EScene} from '@/types/scene.types'
import {toastMessage} from '@repo/shared/Toast'
import {debugLogger} from '@repo/shared/utils/debugLogger'
import axios from 'axios'
import {useAtom, useSetAtom} from 'jotai'
import {useCallback, useState} from 'react'
import {appEnv} from '../../appEnv'
import {EGameMode, gameCodeState, gameModeState, sceneState} from '../Store/atoms'
import FrameStore from '../Store/FrameStore'

const fStore = new FrameStore()
interface IGameCodeData {
  gameCode: number
}

export const useGameCode = () => {
  const [gameCode, setGameCode] = useAtom(gameCodeState)
  const setScene = useSetAtom(sceneState)
  const setGameMode = useSetAtom(gameModeState)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  /**
   * Get a game code from the server
   */
  const getCode = useCallback(async () => {
    if (gameCode) {
      debugLogger({
        message: 'Game code already exists',
        tag: 'useGameCode',
      })
      return
    }

    setIsLoading(true)

    axios
      .post<IGameCodeData>(`${appEnv.API_URL}/createGame`)
      .then(res => {
        const gameCode = res.data.gameCode
        setGameCode(res.data.gameCode)
        setGameMode(EGameMode.PARTY)
        fStore.setGameMode(EGameMode.PARTY)
        setScene(EScene.LOBBY)
        setError(undefined)
        debugLogger({
          message: 'Retrieved game code',
          tag: 'useGameCode',
          data: gameCode,
        })
      })
      .catch(error => {
        debugLogger({
          message: 'Error retrieving game code',
          tag: 'useGameCode',
          data: error,
        })
        toastMessage.error('Noe gikk galt under opprettelse av spill. Prøv igjen senere.')

        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          // console.log(error.response.data)
          // console.log(error.response.status)
          // console.log(error.response.headers)
          setError('Serveren kunne ikke starte et nytt spill. Vennligst prøv igjen senere.')
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          // console.log(error.request)
          setError('Får ikke kontakt med spillserveren. Vennligst prøv igjen senere.')
        } else {
          // Something happened in setting up the request that triggered an Error
          // console.log('Error', error.message)
          setError(error.message)
        }

        // console.log(error.config)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [gameCode])

  /**
   * Destroy the game code
   */
  const destroyCode = useCallback(() => {
    debugLogger({
      message: 'Destroying game code',
      tag: 'useGameCode',
      data: gameCode,
    })
    setError(undefined)
    // TODO: Let server know that the game code is no longer in use?
  }, [gameCode])

  return {getCode, destroyCode, error, isLoading}
}
